import React from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ViewModeTitle} from 'app/general/detailpage/ViewModeTitle';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import {StickySidebar} from 'app/procedures/components/StickySidebar';
import {gt} from 'app/utils/i18n';

import {LoadableBaseDiagram} from './BaseDiagram';
import {makeDiagramEditable} from './make-editable';
import {PrintDiagramButton} from './PrintDiagramButton';
import {ToggleDiagramButton} from './ToggleDiagramButton';

import type {DocumentVersionContentType} from 'app/acceptance/types';
import type {ApplyDocumentShaperFuncType} from 'app/steps/document-shaper';
import type {AnyStepType} from 'sweet-types';

const StyledContainerDiv = styled.div`
  > div > .btn-toolbar {
    position: absolute;
    left: 10px;
    top: 10px;
  }

  .btn-toolbar,
  .title-buttons {
    .btn {
      font-size: 70%;
    }
  }
`;
const defaultPaperOptions = {
  defaultRouter: {
    name: 'manhattan',
    args: {
      padding: 10,
      startDirections: ['bottom'],
      endDirections: ['top'],
    },
  },
};
const layoutOptions = {
  portrait: {
    nodeSep: 70,
    rankSep: 50,
  },
  landscape: {
    nodeSep: 50,
    rankSep: 70,
  },
};
const generalOptions: {positionContent: string; forceOrientation: 'portrait'} =
  {
    positionContent: 'top',
    forceOrientation: 'portrait',
  };
type Props = {
  onStepClick: (id: string) => void;
  onApplyDocumentShaper?: ApplyDocumentShaperFuncType;
  showingSidebarDiagram: boolean;
  versionId: string | number;
  versionContentType: DocumentVersionContentType;
  steps: Array<AnyStepType>;
  toggleSidebarDiagram: () => void;
  toggleOverlayDiagram: () => void;
  paperOptions?: Record<string, string>;
  elements?: 'view' | 'edit';
  className?: string;
  onCreateStep?: (name: string) => void;
  createStepLabel?: string;
};

export const BaseProcedureDiagramSidebar = ({
  versionId,
  versionContentType,
  steps,
  showingSidebarDiagram,
  toggleSidebarDiagram,
  toggleOverlayDiagram,
  onStepClick,
  elements = 'view',
  paperOptions = {},
  onApplyDocumentShaper,
  onCreateStep,
  createStepLabel,
}: Props) => (
  <BootstrapMinMediumMediaQuery>
    {showingSidebarDiagram ? (
      <LoadableBaseDiagram
        elements={elements}
        onApplyDocumentShaper={onApplyDocumentShaper}
        onStepClick={onStepClick}
        toggleDiagram={toggleSidebarDiagram}
        layoutOptions={layoutOptions}
        paperOptions={{...defaultPaperOptions, ...paperOptions}}
        generalOptions={generalOptions}
        steps={steps}
        onCreateStep={onCreateStep}
        createStepLabel={createStepLabel}
      >
        {(diagram, zoomControl) => (
          <StyledContainerDiv className="d-flex flex-column h-100">
            <ViewModeTitle
              title={gt('Overview')}
              actionsBox={
                <div className="btn-group title-buttons d-print-none">
                  <LazyTooltip tooltip={gt('Print diagram to image')}>
                    <PrintDiagramButton
                      versionId={versionId}
                      versionContentType={versionContentType}
                    />
                  </LazyTooltip>
                  <LazyTooltip tooltip={gt('Expand overview')}>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={toggleOverlayDiagram}
                    >
                      <i className="fas fa-fw fa-expand-arrows-alt" />
                    </button>
                  </LazyTooltip>
                  <LazyTooltip tooltip={gt('Hide overview')}>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={toggleSidebarDiagram}
                    >
                      <i className="fas fa-fw fa-chevron-right" />
                    </button>
                  </LazyTooltip>
                </div>
              }
            />
            <div
              className="flex-grow-1"
              style={{
                position: 'relative',
              }}
            >
              {zoomControl}
              {diagram}
            </div>
          </StyledContainerDiv>
        )}
      </LoadableBaseDiagram>
    ) : (
      <ToggleProcedureDiagramDesktopButton
        toggleDiagram={toggleSidebarDiagram}
      />
    )}
  </BootstrapMinMediumMediaQuery>
);

export const ProcedureDiagramSidebar = ({className, ...props}: Props) => (
  <StickySidebar
    className={cs('d-print-none bg-white', className)}
    onToggleStick={() => undefined}
    height="100vh"
  >
    <BaseProcedureDiagramSidebar {...props} />
  </StickySidebar>
);

export const EditableProcedureDiagramSidebar = makeDiagramEditable(
  BaseProcedureDiagramSidebar
);
type ButtonProps = {
  toggleDiagram: () => void;
};
export const ToggleProcedureDiagramDesktopButton = ({
  toggleDiagram,
}: ButtonProps) => (
  <BootstrapMinMediumMediaQuery className="d-print-none">
    <LazyTooltip tooltip={gt('Show overview')} placement="left">
      <ToggleDiagramButton
        position="top"
        showingDiagram={false}
        toggleDiagram={toggleDiagram}
        iconName={'fa-sitemap'}
      />
    </LazyTooltip>
  </BootstrapMinMediumMediaQuery>
);
